import { useRef, useState, useCallback, useEffect } from 'react'
import type { AnnotTool, AnnotStroke, AnnotPoint } from './useAnnotation'

interface AnnotationOverlayProps {
  strokes: AnnotStroke[]
  onStrokeComplete: (stroke: Omit<AnnotStroke, 'id' | 'author'>) => void
  onUndo: () => void
  onRemoveStroke: (id: string) => void
  onClear: () => void
  enabled: boolean // can this user draw?
  isSharer: boolean
  onToggle?: (enabled: boolean) => void
}

const COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#eab308', '#ffffff', '#000000']
const WIDTHS = [2, 4, 8]
const TOOL_LABELS: Record<AnnotTool, string> = {
  pen: 'Stylo',
  highlighter: 'Surligneur',
  arrow: 'Fleche',
  rectangle: 'Rectangle',
  text: 'Texte',
  eraser: 'Gomme',
}

export function AnnotationOverlay({
  strokes, onStrokeComplete, onUndo, onRemoveStroke, onClear, enabled, isSharer, onToggle,
}: AnnotationOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [tool, setTool] = useState<AnnotTool>('pen')
  const [color, setColor] = useState('#ef4444')
  const [width, setWidth] = useState(4)
  const [showToolbar, setShowToolbar] = useState(true)
  const [textInput, setTextInput] = useState('')
  const [textPos, setTextPos] = useState<AnnotPoint | null>(null)

  const isDrawing = useRef(false)
  const currentPoints = useRef<AnnotPoint[]>([])
  const shapeStart = useRef<AnnotPoint | null>(null)
  const rafRef = useRef<number>(0)

  // Normalise screen coords to 0-1
  const normalise = useCallback((clientX: number, clientY: number): AnnotPoint => {
    const el = containerRef.current
    if (!el) return { x: 0, y: 0 }
    const rect = el.getBoundingClientRect()
    return {
      x: Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (clientY - rect.top) / rect.height)),
    }
  }, [])

  // Draw all strokes on canvas
  const redraw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const w = canvas.width
    const h = canvas.height
    ctx.clearRect(0, 0, w, h)

    for (const s of strokes) {
      drawStroke(ctx, s, w, h)
    }

    // Draw in-progress stroke
    if (isDrawing.current && currentPoints.current.length > 0) {
      drawStroke(ctx, {
        id: '_current', tool, points: currentPoints.current,
        color, width, author: '',
      }, w, h)
    }
  }, [strokes, tool, color, width])

  // Resize canvas to match container
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return
    const rect = container.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    canvas.style.width = `${rect.width}px`
    canvas.style.height = `${rect.height}px`
    redraw()
  }, [redraw])

  useEffect(() => {
    resizeCanvas()
    const obs = new ResizeObserver(resizeCanvas)
    if (containerRef.current) obs.observe(containerRef.current)
    return () => obs.disconnect()
  }, [resizeCanvas])

  useEffect(() => { redraw() }, [redraw])

  // Get position from event
  const getPos = (e: React.MouseEvent | React.TouchEvent): AnnotPoint => {
    if ('touches' in e) {
      const t = e.touches[0] || (e as React.TouchEvent).changedTouches[0]
      return normalise(t.clientX, t.clientY)
    }
    return normalise(e.clientX, e.clientY)
  }

  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (!enabled) return
    e.preventDefault()
    e.stopPropagation()
    const pos = getPos(e)

    if (tool === 'text') {
      setTextPos(pos)
      return
    }

    if (tool === 'eraser') {
      // Find and remove stroke near click
      eraseAt(pos)
      return
    }

    isDrawing.current = true
    currentPoints.current = [pos]
    shapeStart.current = pos
  }

  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing.current || !enabled) return
    e.preventDefault()
    e.stopPropagation()
    const pos = getPos(e)
    currentPoints.current.push(pos)
    cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(redraw)
  }

  const handleEnd = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing.current) return
    e.preventDefault()
    e.stopPropagation()
    isDrawing.current = false

    if (currentPoints.current.length < 2 && tool !== 'arrow' && tool !== 'rectangle') return

    if (tool === 'arrow' && currentPoints.current.length >= 2) {
      // Arrow: only keep start and end
      const pts = currentPoints.current
      onStrokeComplete({
        tool: 'arrow',
        points: [pts[0], pts[pts.length - 1]],
        color, width,
      })
    } else if (tool === 'rectangle' && currentPoints.current.length >= 2) {
      const pts = currentPoints.current
      onStrokeComplete({
        tool: 'rectangle',
        points: [pts[0], pts[pts.length - 1]],
        color, width,
      })
    } else {
      onStrokeComplete({
        tool, points: [...currentPoints.current], color, width,
      })
    }

    currentPoints.current = []
    redraw()
  }

  const eraseAt = (pos: AnnotPoint) => {
    // Hit-test: find closest stroke within threshold
    const threshold = 0.03 // 3% of canvas
    let bestDist = Infinity
    let bestId: string | null = null
    for (let i = strokes.length - 1; i >= 0; i--) {
      const s = strokes[i]
      for (const p of s.points) {
        const dist = Math.hypot(p.x - pos.x, p.y - pos.y)
        if (dist < threshold && dist < bestDist) {
          bestDist = dist
          bestId = s.id
        }
      }
    }
    if (bestId) {
      onRemoveStroke(bestId)
    }
  }

  const commitText = () => {
    if (!textPos || !textInput.trim()) {
      setTextPos(null)
      setTextInput('')
      return
    }
    onStrokeComplete({
      tool: 'text',
      points: [textPos],
      color, width,
      text: textInput.trim(),
    })
    setTextPos(null)
    setTextInput('')
  }

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 z-30"
      style={{ pointerEvents: enabled && showToolbar ? 'auto' : 'none' }}
    >
      {/* Canvas overlay */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ cursor: enabled ? (tool === 'eraser' ? 'crosshair' : 'default') : 'default' }}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
      />

      {/* Text input overlay */}
      {textPos && (
        <div
          className="absolute z-40"
          style={{ left: `${textPos.x * 100}%`, top: `${textPos.y * 100}%` }}
        >
          <input
            autoFocus
            value={textInput}
            onChange={e => setTextInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') commitText(); if (e.key === 'Escape') { setTextPos(null); setTextInput('') } }}
            onBlur={commitText}
            className="bg-zinc-900/90 border border-zinc-600 text-white text-xs px-2 py-1 rounded outline-none min-w-[120px]"
            placeholder="Texte..."
          />
        </div>
      )}

      {/* Floating toolbar */}
      {showToolbar && enabled && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-40 flex items-center gap-1 bg-zinc-900/90 border border-zinc-700 rounded-lg px-2 py-1.5 shadow-xl backdrop-blur-sm">
          {/* Tools */}
          {(Object.keys(TOOL_LABELS) as AnnotTool[]).map(t => (
            <button
              key={t}
              onClick={() => setTool(t)}
              className={`px-2 py-1 text-[10px] rounded transition-colors ${
                tool === t ? 'bg-blue-600 text-white' : 'text-zinc-400 hover:bg-zinc-700'
              }`}
              title={TOOL_LABELS[t]}
            >
              {TOOL_LABELS[t]}
            </button>
          ))}

          <div className="w-px h-5 bg-zinc-700 mx-1" />

          {/* Colors */}
          {COLORS.map(c => (
            <button
              key={c}
              onClick={() => setColor(c)}
              className={`w-5 h-5 rounded-full border-2 transition-transform ${
                color === c ? 'border-white scale-110' : 'border-zinc-600'
              }`}
              style={{ backgroundColor: c }}
            />
          ))}

          <div className="w-px h-5 bg-zinc-700 mx-1" />

          {/* Widths */}
          {WIDTHS.map(w => (
            <button
              key={w}
              onClick={() => setWidth(w)}
              className={`w-6 h-6 flex items-center justify-center rounded ${
                width === w ? 'bg-zinc-600' : 'hover:bg-zinc-700'
              }`}
              title={`${w}px`}
            >
              <div className="rounded-full bg-white" style={{ width: w + 2, height: w + 2 }} />
            </button>
          ))}

          <div className="w-px h-5 bg-zinc-700 mx-1" />

          {/* Actions */}
          <button onClick={onUndo} className="px-2 py-1 text-[10px] text-zinc-400 hover:bg-zinc-700 rounded" title="Annuler">
            Annuler
          </button>
          {isSharer && (
            <button onClick={onClear} className="px-2 py-1 text-[10px] text-red-400 hover:bg-zinc-700 rounded" title="Effacer tout">
              Effacer
            </button>
          )}
          {isSharer && onToggle && (
            <button
              onClick={() => onToggle(!enabled)}
              className="px-2 py-1 text-[10px] text-yellow-400 hover:bg-zinc-700 rounded"
              title="Activer/desactiver annotations"
            >
              {enabled ? 'Desactiver' : 'Activer'}
            </button>
          )}
        </div>
      )}

      {/* Toggle toolbar visibility */}
      <button
        onClick={() => setShowToolbar(t => !t)}
        className="absolute top-2 right-2 z-40 w-7 h-7 rounded bg-zinc-900/80 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white text-xs"
        style={{ pointerEvents: 'auto' }}
        title={showToolbar ? 'Masquer outils' : 'Afficher outils'}
      >
        {showToolbar ? 'x' : 'A'}
      </button>
    </div>
  )
}

// ── Stroke rendering ──

function drawStroke(
  ctx: CanvasRenderingContext2D,
  stroke: AnnotStroke,
  canvasW: number,
  canvasH: number,
) {
  const dpr = window.devicePixelRatio || 1
  const w = canvasW / dpr
  const h = canvasH / dpr

  ctx.save()
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.scale(dpr, dpr)

  const toX = (p: AnnotPoint) => p.x * w
  const toY = (p: AnnotPoint) => p.y * h

  ctx.strokeStyle = stroke.color
  ctx.fillStyle = stroke.color
  ctx.lineWidth = stroke.width
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  if (stroke.tool === 'highlighter') {
    ctx.globalAlpha = 0.35
    ctx.lineWidth = stroke.width * 3
  }

  if (stroke.tool === 'pen' || stroke.tool === 'highlighter') {
    if (stroke.points.length < 2) { ctx.restore(); return }
    ctx.beginPath()
    ctx.moveTo(toX(stroke.points[0]), toY(stroke.points[0]))
    for (let i = 1; i < stroke.points.length; i++) {
      ctx.lineTo(toX(stroke.points[i]), toY(stroke.points[i]))
    }
    ctx.stroke()
  } else if (stroke.tool === 'arrow') {
    if (stroke.points.length < 2) { ctx.restore(); return }
    const start = stroke.points[0]
    const end = stroke.points[stroke.points.length - 1]
    const sx = toX(start), sy = toY(start)
    const ex = toX(end), ey = toY(end)

    // Line
    ctx.beginPath()
    ctx.moveTo(sx, sy)
    ctx.lineTo(ex, ey)
    ctx.stroke()

    // Arrowhead
    const angle = Math.atan2(ey - sy, ex - sx)
    const headLen = 12 + stroke.width * 2
    ctx.beginPath()
    ctx.moveTo(ex, ey)
    ctx.lineTo(ex - headLen * Math.cos(angle - 0.4), ey - headLen * Math.sin(angle - 0.4))
    ctx.moveTo(ex, ey)
    ctx.lineTo(ex - headLen * Math.cos(angle + 0.4), ey - headLen * Math.sin(angle + 0.4))
    ctx.stroke()
  } else if (stroke.tool === 'rectangle') {
    if (stroke.points.length < 2) { ctx.restore(); return }
    const p0 = stroke.points[0]
    const p1 = stroke.points[stroke.points.length - 1]
    ctx.beginPath()
    ctx.rect(toX(p0), toY(p0), toX(p1) - toX(p0), toY(p1) - toY(p0))
    ctx.stroke()
  } else if (stroke.tool === 'text' && stroke.text) {
    const fontSize = Math.max(12, stroke.width * 4)
    ctx.font = `${fontSize}px sans-serif`
    ctx.fillText(stroke.text, toX(stroke.points[0]), toY(stroke.points[0]))
  }

  ctx.restore()
}
